import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchOntologyClasses,
  fetchFactFindOntology,
  fetchFactFindClientGraph,
  fillFactFindFormManual,
  fillFactFindFormMicrophone,
} from "../services/ontology.service";
import {
  fetchActiveFactFind,
  saveFactFind,
  updateFactFindGraph,
  exportFactFind,
} from "../services/factfind.service";
import { fetchTranscribeDataSchema } from "../services/meetings.service";
import { fetchPythonGraphData } from "../services/knowledgeGraph.service";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MicIcon from "@mui/icons-material/Mic";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import HomeIcon from "@mui/icons-material/Home";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import FlagIcon from "@mui/icons-material/Flag";
import WorkIcon from "@mui/icons-material/Work";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import FolderIcon from "@mui/icons-material/Folder";
import json from "./factfind.json";
import { DataContext } from "../contexts/DataProvider";
import {
  Box,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Button,
  TableCell,
  TableRow,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAlert } from "../functions/useAlert";
import BackButton from "../common/BackButton";
import HistoryIcon from "@mui/icons-material/History";
import { FactFindVersionHistoryDrawer } from "../components/FactFindVersionHistoryDrawer";
import ImportFactFindModal from "./ImportFactFindModal";
import FactFindFormSkeleton from "./FactFindFormSkeleton";
import { AI_FACT_FIND_CHANGED_EVENT } from "../services/AgentChatClient";

// --- START: HELPER FUNCTIONS ---

/**
 * 1. KEY NORMALIZER (With Schema Overrides)
 * Maps loose frontend labels (e.g., "Interest Rate %") to strict Mongoose Schema keys (e.g., "interestRatePercent").
 * Crucial for preventing "ghost changes" where data is sent but rejected by the backend.
 */
const getMongooseKey = (label: string): string => {
  if (!label) return "";

  const lower = label.toLowerCase().trim();

  // --- MANUAL OVERRIDES (Fixes Infinite Loops) ---
  if (lower === "riskprofile") return "riskprofile";
  if (lower === "employment") return "employment";

  if (
    lower === "annual interest rate" ||
    lower === "annual interest rate (%)" ||
    lower === "annual interest rate %"
  ) {
    return "annualInterestRatePercent";
  }

  if (lower === "insured person(s)" || lower === "insured person") {
    return "insuredPersons";
  }

  // Fix: Backend schema expects 'interestRatePercent', frontend usually sends 'Interest Rate'
  if (
    lower === "interest rate" ||
    lower === "interest rate (%)" ||
    lower === "interest rate %"
  ) {
    return "interestRatePercent";
  }
  // -------------------------

  return label
    .replace(/[^a-zA-Z0-9]+(.)?/g, (_match, chr) =>
      chr ? chr.toUpperCase() : ""
    )
    .replace(/^./, (str) => str.toLowerCase());
};

/**
 * Strips noise (has, annual, %, numbers) from keys to allow "Fuzzy Finding" in the deep graph.
 */
const getCanonicalKey = (key: string) => {
  if (!key) return "";
  let clean = key.toLowerCase();
  clean = clean.replace(/^has/, "");
  clean = clean.replace(/110/g, "");
  clean = clean.replace(/annual/g, "");
  clean = clean.replace(/monthly/g, "");
  clean = clean.replace(/percent/g, "");
  clean = clean.replace(/[0-9-]/g, "");
  clean = clean.replace(/[^a-z]/g, "");
  return clean;
};

/**
 * 2. RECURSIVE DEEP FINDER
 * Searches the Graph Data tree depth-first to find a matching Canonical Key.
 * Allows finding data even if the nesting structure changes slightly.
 */
const findDeepValue = (
  node: any,
  targetCanonical: string,
  depth: number
): any => {
  if (depth <= 0 || !node || typeof node !== "object") return null;

  for (const key of Object.keys(node)) {
    if (getCanonicalKey(key) === targetCanonical) return node[key];
  }

  for (const key of Object.keys(node)) {
    if (typeof node[key] === "object") {
      const res = findDeepValue(node[key], targetCanonical, depth - 1);
      if (res) return res;
    }
  }
  return null;
};

/**
 * 3. STRICT NORMALIZATION
 * Ensures primitives are strings (or "N/A") for consistent DB comparison.
 * Fix: Explicitly handles boolean `false` so it isn't lost as "N/A".
 */
const normalizeAndSortData = (obj: any): any => {
  if (obj === null || obj === undefined) return "N/A";

  if (typeof obj !== "object") {
    if (typeof obj === "boolean") return obj ? "true" : "false";
    const strVal = String(obj).trim();
    if (strVal === "" || strVal.toLowerCase() === "unknown") return "N/A";
    return strVal;
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) return [];
    // Stable sort for string comparison (detects real changes vs order changes)
    return obj.map(normalizeAndSortData).sort((a, b) => {
      return JSON.stringify(a).localeCompare(JSON.stringify(b));
    });
  }

  const result: any = {};
  const keys = Object.keys(obj).sort();

  for (const key of keys) {
    // Strip metadata to prevent false positives in change detection
    if (
      [
        "_id",
        "__v",
        "createdAt",
        "updatedAt",
        "version",
        "clientId",
        "status",
        "fileName",
        "job_id",
      ].includes(key)
    )
      continue;
    result[key] = normalizeAndSortData(obj[key]);
  }

  return result;
};

/**
 * MAIN TRANSFORM FUNCTION
 * Bridges Raw Graph Data -> Clean UI Payload.
 * Includes "Smart Array Detection" to handle both Lists (Goals) and Singletons (Liabilities).
 */
const transformGraphDataToPayload = (
  categories: any[],
  graphDataNode: any
): { [key: string]: any } => {
  const payload: { [key: string]: any } = {};
  if (!categories || !graphDataNode) return payload;

  categories.forEach((category) => {
    if (!category.blocks) return;

    category.blocks.forEach((block: any) => {
      const blockKey = getMongooseKey(block.section);
      const sectionCanonical = getCanonicalKey(block.section);

      // 1. Find the Context Node (e.g. "income", "liabilities")
      let contextNode = findDeepValue(graphDataNode, sectionCanonical, 10);

      // Fallbacks for known tough fields
      if (!contextNode) {
        if (blockKey === "income")
          contextNode = findDeepValue(graphDataNode, "income", 15);
        if (blockKey === "riskprofile")
          contextNode = findDeepValue(graphDataNode, "riskprofile", 15);
        if (blockKey === "assets")
          contextNode = findDeepValue(graphDataNode, "assets", 15);
        if (blockKey === "liabilities")
          contextNode = findDeepValue(graphDataNode, "liabilities", 15);
      }

      // 2. Recursive Field Extractor
      const extractFields = (fields: any[], node: any): any => {
        const item: any = {};
        fields.forEach((field) => {
          if (field.subsection_name) {
            const subKey = getMongooseKey(field.subsection_name);
            const subNode = findDeepValue(
              node,
              getCanonicalKey(field.subsection_name),
              5
            );
            item[subKey] = extractFields(field.fields, subNode || node);
          } else if (field.displayLabel) {
            const fieldKey = getMongooseKey(field.displayLabel);
            const fieldCanonical = getCanonicalKey(field.displayLabel);

            // First try to find it in the current node (nested location)
            let val = findDeepValue(node, fieldCanonical, 5);

            // If not found or is N/A, check root level of graphDataNode
            // Many fields are stored at root level: hasLabel, hasRole, etc.
            if (!val || val === "N/A" || val === null || val === undefined) {
              // Try common field patterns at root level
              const hasFieldName = `has${field.displayLabel.replace(
                /\s+/g,
                ""
              )}`;
              const rootChecks = [
                graphDataNode?.[hasFieldName], // hasLabel, hasRole
                graphDataNode?.[fieldKey], // label, role
                graphDataNode?.[field.displayLabel], // Label, Role
                graphDataNode?.[fieldCanonical], // canonicalized version
              ];

              for (const rootVal of rootChecks) {
                if (
                  rootVal &&
                  rootVal !== "N/A" &&
                  rootVal !== null &&
                  rootVal !== undefined
                ) {
                  val = rootVal;
                  if (fieldKey === "role" || fieldKey === "label") {
                    console.log(`   ✅ Found value: "${val}"`);
                  }
                  break;
                }
              }
            }

            item[fieldKey] = normalizeAndSortData(val);
          }
        });
        return item;
      };

      // 3. Handle Arrays (Smart Detection)
      if (block.type === "array") {
        let itemsArray: any[] = [];

        if (contextNode) {
          if (Array.isArray(contextNode)) {
            itemsArray = contextNode;
          } else if (typeof contextNode === "object") {
            // Check if keys look like IDs (e.g. "goal-1", "income-2")
            const keys = Object.keys(contextNode);
            const isListOfItems = keys.some((k) => /\d/.test(k));

            if (isListOfItems) {
              // It's a Dictionary List (e.g. Goals) -> Map values
              itemsArray = Object.values(contextNode);
            } else {
              // It's a Container (e.g. Liabilities) -> Keep as Single Row
              itemsArray = [contextNode];
            }
          }
        }

        if (itemsArray.length > 0) {
          payload[blockKey] = itemsArray.map((node) =>
            extractFields(block.fields, node)
          );
        } else {
          payload[blockKey] = [];
        }
      } else {
        // Handle Single Objects (e.g. Personal Details)
        payload[blockKey] = extractFields(
          block.fields,
          contextNode || graphDataNode
        );
      }
    });
  });

  return payload;
};
// --- END: HELPER FUNCTIONS ---

const AccordionTable = ({
  body,
  label,
  hide,
  onRemove,
  ...props
}: {
  body: React.ReactNode;
  label: string;
  hide?: boolean;
  onRemove?: () => void;
}) => {
  if (hide) return;
  return (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        marginTop: 0,
        marginBottom: 0,
        border: "1px solid #e0e0e0",
        "&:not(:last-child)": { borderBottom: 0 },
        "&:before": { display: "none" },
        "&:.MuiAccordion-root:first-of-type": { borderRadius: "0 0 0 0" },
        "&:.MuiAccordion-root:last-of-type": { borderRadius: "0 0 8px 8px" },
        "&:.Mui-expanded": {
          margin: "0 !important",
          "&:not(:last-child)": { borderBottom: 0 },
        },
      }}
      {...props}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: "#f5f5f5",
          "&.Mui-expanded": { backgroundColor: "#e0e0e0" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {label}
          </Typography>
          {onRemove && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={{ mr: 1, color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "0 0 8px 8px",
            overflow: "hidden",
            marginTop: "-1px",
            borderTop: "1px solid #e0e0e0",
            boxShadow: "none",
          }}
        >
          <Table>
            <TableBody>{body}</TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

function formatField(key: string) {
  if (!key || typeof key !== "string") return "";
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
}


const SimpleTableCell = ({
  field,
  value,
  onChange,
}: {
  field: any;
  value: string;
  onChange: (newValue: string) => void;
}) => {
  if (!field) return null;
  return (
    <TableRow>
      <TableCell
        sx={{
          marginTop: "16px",
          fontWeight: "bold",
          border: "1px solid #ccc",
          backgroundColor: "#f9f9f9",
          width: "50%",
        }}
      >
        {formatField(field.displayLabel)}
      </TableCell>
      <TableCell
        sx={{
          border: "1px solid #ccc",
          backgroundColor: "#eaf4ff",
          fontSize: "16px",
          padding: "12px",
          width: "50%",
        }}
      >
        <TextField
          value={value === null || value === undefined ? "" : value}
          onChange={(e) => onChange(e.target.value)}
          variant="standard"
          fullWidth
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "16px",
              padding: 0,
              "& input": { padding: 0, backgroundColor: "transparent" },
            },
          }}
        />
      </TableCell>
    </TableRow>
  );
};

const DynamicAccordionFields = ({
  data,
  blockSchema,
  blockKey,
  handleFieldChange,
  handleAddItem,
  handleRemoveItem,
}: {
  data: any;
  blockSchema: any;
  blockKey: string;
  handleFieldChange: (
    blockKey: string,
    itemIndex: number | null,
    path: string[],
    value: string
  ) => void;
  handleAddItem: (blockKey: string) => void;
  handleRemoveItem: (blockKey: string, index: number) => void;
}) => {
  const HIDDEN_FACT_FIND_FIELDS = new Set([
    "createdby", "updatedby", "orgid", "version", "createdat", "updatedat",
    "_id", "__v", "clientid", "status", "jobid", "job_id",
  ]);

  const isInternalIdField = (fieldKey: string, value: any): boolean => {
    // Field key ends with 'id' (e.g. meetingId, transcriptionId, chunkId, orgId)
    if (/id$/i.test(fieldKey)) return true;
    if (value === null || value === undefined || value === "" || value === "—") return false;
    const str = String(value);
    // MongoDB ObjectId: 24 hex characters
    if (/^[0-9a-f]{24}$/i.test(str)) return true;
    // Chunk ID pattern
    if (/^chunk_/i.test(str)) return true;
    return false;
  };

  const renderSubFields = (
    dataItem: any,
    schemaFields: any[],
    itemIndex: number | null,
    path: string[]
  ) => {
    if (!schemaFields) return null;
    return schemaFields.map((field: any, idx: number) => {
      if (field.subsection_name && Array.isArray(field.fields)) {
        const subKey = getMongooseKey(field.subsection_name);
        const subDataItem = dataItem ? dataItem[subKey] : null;
        return (
          <TableRow key={field.subsection_name + idx}>
            <TableCell colSpan={2} sx={{ padding: 0, border: "none" }}>
              <AccordionTable
                label={formatField(field.subsection_name)}
                body={
                  <Table>
                    <TableBody>
                      {renderSubFields(subDataItem, field.fields, itemIndex, [
                        ...path,
                        subKey,
                      ])}
                    </TableBody>
                  </Table>
                }
              />
            </TableCell>
          </TableRow>
        );
      }
      if (field?.displayLabel) {
        const fieldKey = getMongooseKey(field.displayLabel);
        if (HIDDEN_FACT_FIND_FIELDS.has(fieldKey.toLowerCase())) return null;
        const value = dataItem ? dataItem[fieldKey] : "";
        if (isInternalIdField(fieldKey, value)) return null;
        return (
          <SimpleTableCell
            key={field.displayLabel || idx}
            field={field}
            value={value}
            onChange={(newValue) => {
              handleFieldChange(
                blockKey,
                itemIndex,
                [...path, fieldKey],
                newValue
              );
            }}
          />
        );
      }
      return null;
    });
  };

  if (!blockSchema) return null;

  return (
    <>
      {blockSchema.type === "array" ? (
        <>
          {data &&
            data.length > 0 &&
            data.map((dataItem: any, i: number) => (
              <AccordionTable
                key={`${blockSchema.section}-${i}`}
                label={`${formatField(blockSchema.section)} ${i + 1}`}
                onRemove={() => handleRemoveItem(blockKey, i)}
                body={
                  <Table>
                    <TableBody>
                      {renderSubFields(dataItem, blockSchema.fields, i, [])}
                    </TableBody>
                  </Table>
                }
              />
            ))}
          {(!data || data.length === 0) && (
            <AccordionTable
              key={`${blockSchema.section}-0`}
              label={`${formatField(blockSchema.section)} 1`}
              body={
                <Table>
                  <TableBody>
                    {renderSubFields(null, blockSchema.fields, 0, [])}
                  </TableBody>
                </Table>
              }
            />
          )}
          <Box
            sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "flex-start" }}
          >
            <Button variant="outlined" onClick={() => handleAddItem(blockKey)}>
              Add {formatField(blockSchema.section)}
            </Button>
          </Box>
        </>
      ) : (
        <AccordionTable
          key={blockSchema.section}
          label={formatField(blockSchema.section)}
          body={
            <Table>
              <TableBody>
                {renderSubFields(data, blockSchema.fields, null, [])}
              </TableBody>
            </Table>
          }
        />
      )}
    </>
  );
};

const stripHas = (label: string): string => {
  return label.replace(/^has\s+/i, "").replace(/^has([A-Z])/g, "$1");
};

/**
 * Build factfindTemplate schema from the same two APIs used by the Manage Template tab:
 *   GET /ontology/classes  → all available classes with their properties
 *   GET /ontology/factfind → org's saved selection (which classes/props to show)
 *
 * Each selected class becomes a tab; its selected properties become form fields.
 * If no saved selection exists, all classes and all properties are shown.
 */
const buildSchemaFromOntologySelection = (classes: any[], selected: any[]): any => {
  if (!classes || classes.length === 0) return null;

  const useAll = !selected || selected.length === 0;

  // Build a fast lookup: class_iri → Set of selected property IRIs
  const selectionMap = new Map<string, Set<string>>();
  if (!useAll) {
    selected.forEach((s: any) => {
      selectionMap.set(s.class_iri, new Set(s.properties));
    });
  }

  const categories: any[] = [];

  classes.forEach((cls: any) => {
    // Skip classes not in the org's selection (unless showing all)
    if (!useAll && !selectionMap.has(cls.iri)) return;

    const selectedPropIris = useAll ? null : selectionMap.get(cls.iri)!;
    const filteredProps = useAll
      ? (cls.properties || [])
      : (cls.properties || []).filter((p: any) => selectedPropIris!.has(p.iri));

    if (filteredProps.length === 0 && !useAll) return;

    const className = stripHas(cls.label || cls.iri?.split("#").pop() || "Unknown");

    categories.push({
      name: className.toUpperCase(),
      blocks: [{
        section: className,
        type: "object",
        fields: filteredProps.map((prop: any) => ({
          displayLabel: stripHas(prop.label || prop.iri?.split("#").pop() || ""),
          graphLabel: prop.iri || "",
          type: prop.range || "string",
          jsonPath: "",
        })),
      }],
    });
  });

  return categories.length > 0 ? { factfindTemplate: { categories } } : null;
};

// ── ClientGraphTree ────────────────────────────────────────────────────────────
// Renders the /factfind/clientgraph response:
// { data: [{ class_iri, instances: [{ id, uri, properties: { iri: value } }] }] }

// Extract the local name after # or last /
const extractIriLocal = (iri: string): string => {
  const pos = Math.max(iri.lastIndexOf("#"), iri.lastIndexOf("/"));
  return pos >= 0 ? iri.slice(pos + 1) : iri;
};

// camelCase / PascalCase → "Title Case Words"
const iriToLabel = (iri: string): string => {
  const local = extractIriLocal(iri);
  return local
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

const getClassIcon = (label: string) => {
  const l = label.toLowerCase();
  if (l.includes("vehicle")) return <DirectionsCarIcon sx={{ fontSize: 15 }} />;
  if (l.includes("primary property") || l.includes("home loan") || l.includes("mortgage")) return <HomeIcon sx={{ fontSize: 15 }} />;
  if (l.includes("investment property")) return <ApartmentIcon sx={{ fontSize: 15 }} />;
  if (l.includes("investment asset") || l.includes("future income")) return <TrendingUpIcon sx={{ fontSize: 15 }} />;
  if (l.includes("liquid") || l.includes("superannuation")) return <AccountBalanceIcon sx={{ fontSize: 15 }} />;
  if (l.includes("business")) return <BusinessCenterIcon sx={{ fontSize: 15 }} />;
  if (l.includes("insurance")) return <SecurityIcon sx={{ fontSize: 15 }} />;
  if (l.includes("goal")) return <FlagIcon sx={{ fontSize: 15 }} />;
  if (l.includes("income") || l.includes("expense")) return <AttachMoneyIcon sx={{ fontSize: 15 }} />;
  if (l.includes("personal") || l.includes("contact") || l.includes("health")) return <PersonIcon sx={{ fontSize: 15 }} />;
  if (l.includes("employment")) return <WorkIcon sx={{ fontSize: 15 }} />;
  if (l.includes("loan") || l.includes("liabilit") || l.includes("credit") || l.includes("other")) return <CreditCardIcon sx={{ fontSize: 15 }} />;
  if (l.includes("risk")) return <AssessmentIcon sx={{ fontSize: 15 }} />;
  if (l.includes("tax") || l.includes("deduction") || l.includes("repayment")) return <ReceiptIcon sx={{ fontSize: 15 }} />;
  return <FolderIcon sx={{ fontSize: 15 }} />;
};

interface ClientGraphTreeProps {
  data: any;
  orgId: string;
  clientId: string;
  onRefresh: () => void;
  onPersist: (updatedGraph: any) => Promise<void>;
}

const ClientGraphTree: React.FC<ClientGraphTreeProps> = ({ data, orgId, clientId, onRefresh, onPersist }) => {
  const [activeSectionKey, setActiveSectionKey] = useState("0-0");
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, Record<number, Record<string, string>>>>({});
  const [recordingKey, setRecordingKey] = useState<string | null>(null);
  const [isSendingMic, setIsSendingMic] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [changedByInstance, setChangedByInstance] = useState<
    Record<string, { class_iri: string; fields: Record<string, string> }>
  >({});
  const recognitionRef = useRef<any>(null);
  const transcriptRef = useRef<string>("");
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const allSections = useMemo(() => {
    const tabs: any[] = data?.data || [];
    const sections: Array<{
      key: string;
      tabIdx: number;
      classIdx: number;
      tabName: string;
      cls: any;
      clsLabel: string;
    }> = [];
    tabs.forEach((tab: any, tabIdx: number) => {
      (tab.classes || []).forEach((cls: any, classIdx: number) => {
        sections.push({
          key: `${tabIdx}-${classIdx}`,
          tabIdx,
          classIdx,
          tabName: tab.tab || `Group ${tabIdx + 1}`,
          cls,
          clsLabel: cls.label || iriToLabel(cls.class_iri),
        });
      });
    });
    return sections;
  }, [data]);

  const tabs: any[] = data?.data || [];

  if (allSections.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No class data returned from the graph API.
      </Typography>
    );
  }

  const handleSidebarClick = (key: string) => {
    setActiveSectionKey(key);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  };

  const getValue = (sectionKey: string, instIdx: number, propIri: string, raw: any): string => {
    const override = localValues[sectionKey]?.[instIdx]?.[propIri];
    if (override !== undefined) return override;
    return raw !== "" && raw !== null && raw !== undefined ? String(raw) : "";
  };

  const handleChange = (sectionKey: string, instIdx: number, propIri: string, val: string, instanceUri: string, classIri: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [instIdx]: { ...(prev[sectionKey]?.[instIdx] ?? {}), [propIri]: val } },
    }));
    setChangedByInstance((prev) => ({
      ...prev,
      [instanceUri]: {
        class_iri: classIri,
        fields: { ...(prev[instanceUri]?.fields ?? {}), [propIri]: val },
      },
    }));
  };

  const handleSaveAndContinue = async (_sectionKey: string) => {
    const entries = Object.entries(changedByInstance);
    if (entries.length === 0) {
      setEditingSection(null);
      return;
    }
    setIsSaving(true);
    try {
      await Promise.all(
        entries.map(([instanceUri, { class_iri, fields }]) =>
          fillFactFindFormManual({ org_id: orgId, client_id: clientId, class_iri, instance_uri: instanceUri, fields })
        )
      );
      setChangedByInstance({});
      setEditingSection(null);

      // Fetch updated graph, refresh UI, then persist new version to MongoDB
      try {
        const updatedGraph = await fetchFactFindClientGraph(orgId, clientId);
        onRefresh();
        await onPersist(updatedGraph);
      } catch (persistErr) {
        console.error("Failed to persist fact-find version after save:", persistErr);
        onRefresh();
      }
    } catch (err) {
      console.error("Failed to save manual fields:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMicClick = (sectionKey: string, instIdx: number, instance: any, capturedClass: any) => {
    const micKey = `${sectionKey}-${instIdx}`;

    if (recordingKey === micKey) {
      recognitionRef.current?.stop();
      return;
    }
    if (recordingKey !== null) recognitionRef.current?.stop();

    const SpeechRecognitionAPI =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      alert("Speech recognition is not supported in this browser. Please use Chrome.");
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = "en-AU";
    transcriptRef.current = "";
    recognitionRef.current = recognition;
    setRecordingKey(micKey);

    recognition.onresult = (event: any) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) transcriptRef.current += event.results[i][0].transcript + " ";
      }
    };

    recognition.onend = async () => {
      setRecordingKey(null);
      const text = transcriptRef.current.trim();
      if (!text) return;
      const instanceUri: string = instance.uri ?? "";
      const baseUriMatch = instanceUri.match(/^(.*?\/clientgroup\/[^/]+)\//);
      const baseUri = baseUriMatch ? baseUriMatch[1] : instanceUri;
      setIsSendingMic(true);
      try {
        await fillFactFindFormMicrophone({
          org_id: orgId,
          client_id: clientId,
          class_iri: capturedClass.class_iri,
          text,
          instance_uri: instanceUri,
          instances: capturedClass.instances ?? [],
          base_uri: baseUri,
        });
        onRefresh();
      } catch (err) {
        console.error("Microphone API call failed:", err);
      } finally {
        setIsSendingMic(false);
      }
    };

    recognition.onerror = () => setRecordingKey(null);
    recognition.start();
  };

  const showTabGroups = tabs.length > 1;

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 180px)", overflow: "hidden", bgcolor: "#f0f2f5" }}>

      {/* ── LEFT SIDEBAR ──────────────────────────────────────────────── */}
      <Box sx={{
        width: 220, minWidth: 220, bgcolor: "#fff", borderRight: "1px solid #dde1e7",
        overflowY: "auto", flexShrink: 0,
        "&::-webkit-scrollbar": { width: 3 },
        "&::-webkit-scrollbar-thumb": { bgcolor: "#ddd", borderRadius: 2 },
      }}>
        <Typography sx={{
          px: 2.5, py: 1.2, fontSize: "0.65rem", fontWeight: 700, color: "#9aa0a6",
          letterSpacing: 1.2, textTransform: "uppercase",
          borderBottom: "1px solid #f0f2f5", display: "block",
        }}>
          Sections
        </Typography>

        {showTabGroups ? (
          tabs.map((tab: any, tabIdx: number) => (
            <Box key={tabIdx}>
              <Typography sx={{
                px: 2, py: 0.8, fontSize: "0.62rem", fontWeight: 700,
                color: "#9aa0a6", bgcolor: "#f8f9fa",
                textTransform: "uppercase", letterSpacing: 0.8,
                borderBottom: "1px solid #f0f2f5",
              }}>
                {tab.tab}
              </Typography>
              {(tab.classes || []).map((cls: any, classIdx: number) => {
                const key = `${tabIdx}-${classIdx}`;
                const isActive = activeSectionKey === key;
                const clsLabel = cls.label || iriToLabel(cls.class_iri);
                const count = cls.instances?.length ?? 0;
                return (
                  <Box key={key} onClick={() => handleSidebarClick(key)}
                    sx={{
                      display: "flex", alignItems: "center", px: 2, py: 1.3, gap: 1.2,
                      cursor: "pointer", borderBottom: "1px solid #f0f2f5",
                      bgcolor: isActive ? "#1565c0" : "transparent",
                      "&:hover": { bgcolor: isActive ? "#1251a3" : "#f5f7fa" },
                      transition: "background 0.12s",
                    }}>
                    <Box sx={{ color: isActive ? "#fff" : "#80868b", display: "flex", flexShrink: 0 }}>
                      {getClassIcon(clsLabel)}
                    </Box>
                    <Typography fontSize="0.82rem" fontWeight={isActive ? 600 : 400}
                      color={isActive ? "#fff" : "#3c4043"} sx={{ flex: 1, lineHeight: 1.3 }}>
                      {clsLabel}
                    </Typography>
                    {count > 0 && (
                      <Box sx={{
                        bgcolor: isActive ? "rgba(255,255,255,0.22)" : "#e8eaed",
                        color: isActive ? "#fff" : "#5f6368",
                        borderRadius: 10, px: 0.8, fontSize: "0.68rem", fontWeight: 700,
                        minWidth: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {count}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))
        ) : (
          allSections.map(({ key, cls, clsLabel }) => {
            const isActive = activeSectionKey === key;
            const count = cls.instances?.length ?? 0;
            return (
              <Box key={key} onClick={() => handleSidebarClick(key)}
                sx={{
                  display: "flex", alignItems: "center", px: 2, py: 1.3, gap: 1.2,
                  cursor: "pointer", borderBottom: "1px solid #f0f2f5",
                  bgcolor: isActive ? "#1565c0" : "transparent",
                  "&:hover": { bgcolor: isActive ? "#1251a3" : "#f5f7fa" },
                  transition: "background 0.12s",
                }}>
                <Box sx={{ color: isActive ? "#fff" : "#80868b", display: "flex", flexShrink: 0 }}>
                  {getClassIcon(clsLabel)}
                </Box>
                <Typography fontSize="0.82rem" fontWeight={isActive ? 600 : 400}
                  color={isActive ? "#fff" : "#3c4043"} sx={{ flex: 1, lineHeight: 1.3 }}>
                  {clsLabel}
                </Typography>
                {count > 0 && (
                  <Box sx={{
                    bgcolor: isActive ? "rgba(255,255,255,0.22)" : "#e8eaed",
                    color: isActive ? "#fff" : "#5f6368",
                    borderRadius: 10, px: 0.8, fontSize: "0.68rem", fontWeight: 700,
                    minWidth: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {count}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {/* ── RIGHT: Selected section only — avoids mounting thousands of inputs at once. */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1, overflowY: "auto", p: 2,
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#ccc", borderRadius: 3 },
        }}
      >
        {isSendingMic && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, px: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">Processing voice input…</Typography>
          </Box>
        )}

        {allSections.filter(({ key }) => key === activeSectionKey).map(({ key, cls, clsLabel }) => {
          const isEditMode = editingSection === key;

          return (
            <Box
              key={key}
              sx={{
                mb: 2.5, borderRadius: "10px", bgcolor: "#fff",
                border: "1px solid #e0e0e0", overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              }}
            >
              {/* Section header */}
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 2.5, py: 1.5, borderBottom: "1px solid #f0f2f5", bgcolor: "#fafafa",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ color: "#1976d2" }}>{getClassIcon(clsLabel)}</Box>
                  <Typography fontWeight={700} fontSize="0.95rem" color="#1976d2">
                    {clsLabel}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {!isEditMode ? (
                    <Button variant="outlined" size="small" startIcon={<EditIcon />}
                      onClick={() => setEditingSection(key)}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, fontSize: "0.8rem" }}>
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button variant="outlined" size="small"
                        onClick={() => setEditingSection(null)}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, fontSize: "0.8rem" }}>
                        Cancel
                      </Button>
                      <Button variant="contained" size="small"
                        startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        onClick={() => handleSaveAndContinue(key)}
                        disabled={isSaving}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, fontSize: "0.8rem" }}>
                        {isSaving ? "Saving…" : "Save & Continue"}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              {/* Instances */}
              <Box sx={{ p: 2 }}>
                {(cls.instances ?? []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No instances found.</Typography>
                ) : (
                  (cls.instances as any[]).map((instance: any, instIdx: number) => {
                    const micKey = `${key}-${instIdx}`;
                    const instanceUri: string = instance.uri ?? "";
                    const isRecording = recordingKey === micKey;

                    return (
                      <Accordion key={instIdx} disableGutters defaultExpanded
                        sx={{
                          mb: 1.5, border: "1px solid #e0e0e0",
                          borderRadius: "8px !important", "&:before": { display: "none" },
                          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                        }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                          sx={{ bgcolor: "#f5f5f5", borderRadius: "8px 8px 0 0", minHeight: 44 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                            <Typography fontWeight={600} fontSize="0.88rem"
                              sx={{ textTransform: "capitalize", flex: 1 }}>
                              {`${clsLabel} ${instIdx + 1}`}
                            </Typography>
                            {isEditMode && (
                              <Tooltip title={isRecording ? "Stop recording" : "Start recording"}>
                                <IconButton size="small"
                                  onClick={(e) => { e.stopPropagation(); handleMicClick(key, instIdx, instance, cls); }}
                                  sx={{
                                    mr: 1,
                                    color: isRecording ? "#d32f2f" : "#1976d2",
                                    animation: isRecording ? "pulse 1s infinite" : "none",
                                    "@keyframes pulse": {
                                      "0%": { transform: "scale(1)", opacity: 1 },
                                      "50%": { transform: "scale(1.25)", opacity: 0.7 },
                                      "100%": { transform: "scale(1)", opacity: 1 },
                                    },
                                  }}>
                                  <MicIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2 }}>
                          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2 }}>
                            {Object.entries(instance.properties ?? {})
                              .filter(([propIri, rawVal]: [string, any]) => {
                                const localName = extractIriLocal(propIri);
                                if (/id$/i.test(localName)) return false;
                                const str = rawVal !== null && rawVal !== undefined ? String(rawVal) : "";
                                if (/^[0-9a-f]{24}$/i.test(str)) return false;
                                if (/^chunk_/i.test(str)) return false;
                                return true;
                              })
                              .map(([propIri, rawVal]: [string, any]) => (
                                <Box key={propIri}>
                                  <Typography variant="caption" fontWeight={600} color="text.secondary"
                                    sx={{ display: "block", mb: 0.5 }}>
                                    {iriToLabel(propIri)}
                                  </Typography>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    value={getValue(key, instIdx, propIri, rawVal)}
                                    placeholder="—"
                                    onChange={(e) => handleChange(key, instIdx, propIri, e.target.value, instanceUri, cls.class_iri)}
                                    InputProps={{ readOnly: !isEditMode }}
                                    sx={{
                                      "& .MuiInputBase-input": { fontSize: "0.82rem" },
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 1.5,
                                        bgcolor: isEditMode ? "#fff" : "#fafafa",
                                      },
                                    }}
                                  />
                                </Box>
                              ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const ClientDetails = () => {
  const { login }: any = useContext(DataContext);
  const [schema, setSchema] = useState<any>(json);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [setAlert] = useAlert();
  const [factFind, setFactFind] = useState<any>(null);
  const [isVersionDrawerOpen, setIsVersionDrawerOpen] = useState(false);
  const clientId = localStorage.getItem("clientId");
  const [changedFieldsList, setChangedFieldsList] = useState<any[]>([]);
  const [clientGraph, setClientGraph] = useState<any>(null);
  const [clientGraphLoading, setClientGraphLoading] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (format: "docx" | "pdf") => {
    setDownloadAnchor(null);
    if (!clientGraph?.data?.length) { setAlert("No data available for export.", "info"); return; }
    setIsDownloading(true);
    try {
      const response = await exportFactFind({
        graphData: clientGraph,
        format,
        fileName: factFind?.fileName || "FactFind",
        clientName: (factFind?.clientId as any)?.name || factFind?.fileName || "Client",
      });
      if (!response.ok) {
        const err: any = await response.json().catch(() => ({}));
        throw new Error(err.error || "Export failed");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${factFind?.fileName || "FactFind"}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setAlert(`Export failed: ${err.message || "Unknown error"}`, "error");
    } finally {
      setIsDownloading(false);
    }
  };

  // Standalone fetch — called on mount AND after microphone API succeeds
  const fetchClientGraph = async (signal?: AbortSignal) => {
    const orgId = login?.data?.orgId;
    if (!orgId || !clientId) return;
    setClientGraphLoading(true);
    try {
      const data = await fetchFactFindClientGraph(orgId, clientId, signal ?? AbortSignal.timeout(12_000));
      setClientGraph(data);
    } catch (err) {
      if ((err as Error)?.name !== "AbortError" && (err as Error)?.name !== "TimeoutError") {
        console.error("Failed to fetch client graph:", err);
      }
    } finally {
      setClientGraphLoading(false);
    }
  };

  useEffect(() => {
    const handleAgentFactFindChange = (event: Event) => {
      const detail = (event as CustomEvent<{ clientId?: string }>).detail;
      if (!detail?.clientId || detail.clientId === clientId) void fetchClientGraph();
    };
    window.addEventListener(AI_FACT_FIND_CHANGED_EVENT, handleAgentFactFindChange);
    return () => window.removeEventListener(AI_FACT_FIND_CHANGED_EVENT, handleAgentFactFindChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, login?.data?.orgId]);

  useEffect(() => {
    const controller = new AbortController();
    setClientGraph(null);
    fetchClientGraph(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, login?.data?.orgId]);

  // Load org's fact-find schema — same two APIs as the Manage Template tab
  useEffect(() => {
    const fetchOntologySchema = async () => {
      try {
        const orgId = login?.data?.orgId;
        const [classesData, selectionData]: [any, any] = await Promise.all([
          fetchOntologyClasses(orgId),
          fetchFactFindOntology(orgId),
        ]);
        const classes = classesData?.classes || [];
        const selected = selectionData?.selected || [];
        if (classes.length > 0) {
          const converted = buildSchemaFromOntologySelection(classes, selected);
          if (converted) setSchema(converted);
        }
      } catch {
        // Fall back to local JSON schema silently
      }
    };
    fetchOntologySchema();
  }, [login?.data?.orgId]);

  useEffect(() => {
    if (!clientId) {
      setAlert("No client ID found.", "error");
      setLoading(false);
      return;
    }

    const loadFactFindData = async () => {
      try {
        // Page rendering is driven by the authoritative client-graph request above.
        // Load only local metadata here; the old get_graph_data synchronisation was
        // both blocking first paint and writing lossy placeholder versions on open.
        const [schemaResp, activeFactFindResp]: [any, any] =
          await Promise.allSettled([
            fetchTranscribeDataSchema(AbortSignal.timeout(12_000)),
            fetchActiveFactFind(clientId, AbortSignal.timeout(12_000)),
          ]);

        if (schemaResp.status === "fulfilled" && schemaResp.value?.schema) {
          setSchema(schemaResp.value.schema);
        }

        if (activeFactFindResp.status === "fulfilled") {
          setFactFind(activeFactFindResp.value || null);
        }
      } catch (error: any) {
        console.error("Error loading fact-find data:", error);
        setAlert(
          error.message || "An error occurred while loading the fact find.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    loadFactFindData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleFieldChange = (
    blockKey: string,
    itemIndex: number | null,
    path: string[],
    value: string
  ) => {
    setFactFind((prevFactFind: any) => {
      const newFactFind = JSON.parse(JSON.stringify(prevFactFind));
      let dataBlock = newFactFind.factFindData[blockKey];
      let itemToUpdate;

      if (Array.isArray(dataBlock)) {
        const index = itemIndex ?? 0;
        if (!dataBlock[index]) dataBlock[index] = {};
        itemToUpdate = dataBlock[index];
      } else {
        if (dataBlock === null || dataBlock === undefined) {
          newFactFind.factFindData[blockKey] = {};
          dataBlock = newFactFind.factFindData[blockKey];
        }
        itemToUpdate = dataBlock;
      }

      let current = itemToUpdate;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!current[key]) current[key] = {};
        current = current[key];
      }

      // Allow empty strings during editing; normalization handles "N/A" conversion on save.
      current[path[path.length - 1]] = value;

      // Track this field as changed
      const fieldKey = `${blockKey}.${itemIndex ?? "single"}.${path.join(".")}`;
      setChangedFieldsList((prev) => {
        const exists = prev.some((f) => f.key === fieldKey);
        if (exists) {
          return prev.map((f) => (f.key === fieldKey ? { ...f, value } : f));
        }
        return [...prev, { key: fieldKey, blockKey, itemIndex, path, value }];
      });

      return newFactFind;
    });
  };

  const handleAddItem = (blockKey: string) => {
    setFactFind((prevFactFind: any) => {
      const newFactFind = JSON.parse(JSON.stringify(prevFactFind));
      const dataBlock = newFactFind.factFindData[blockKey];

      const factfindtemplate = schema?.factfindTemplate?.categories || [];
      let fieldsToGenerate: any[] = [];
      factfindtemplate.forEach((cat: any) => {
        cat.blocks.forEach((b: any) => {
          if (getMongooseKey(b.section) === blockKey)
            fieldsToGenerate = b.fields;
        });
      });

      const generateEmpty = (fields: any[]) => {
        const obj: any = {};
        fields.forEach((f) => {
          if (f.subsection_name)
            obj[getMongooseKey(f.subsection_name)] = generateEmpty(f.fields);
          else if (f.displayLabel) obj[getMongooseKey(f.displayLabel)] = "N/A";
        });
        return obj;
      };

      const newItem = generateEmpty(fieldsToGenerate);

      if (Array.isArray(dataBlock)) {
        dataBlock.push(newItem);
      } else {
        newFactFind.factFindData[blockKey] = [newItem];
      }
      return newFactFind;
    });
  };

  const handleRemoveItem = (blockKey: string, index: number) => {
    setFactFind((prevFactFind: any) => {
      const newFactFind = JSON.parse(JSON.stringify(prevFactFind));
      const dataBlock = newFactFind.factFindData[blockKey];
      if (Array.isArray(dataBlock) && dataBlock[index] !== undefined) {
        dataBlock.splice(index, 1);
      }
      return newFactFind;
    });
  };

  const handleSaveAndUpdate = async () => {
    if (!clientId || !factFind) return;
    setIsSaving(true);

    try {
      // Get changed fields for sending to graph API
      const changedFields = getChangedFieldsPayload();

      // Normalize payload (Empty -> N/A) before saving to keep DB clean
      const cleanPayload = normalizeAndSortData(factFind.factFindData);

      // STEP 1: Save the current data as a new version (always happens)
      const newVersion = await saveFactFind(clientId, { factFindData: cleanPayload, clientGraphData: clientGraph });

      if (newVersion) {
        setFactFind(newVersion);
        setChangedFieldsList([]); // Clear changed fields after save
        setAlert("Data saved successfully", "success");

        // STEP 2: If there are changed fields, send to graph API
        if (changedFields.length > 0) {
          try {
            await updateFactFindGraph({
              repo_id: "prospo-alpha",
              client_id: clientId,
              fields: changedFields,
            });

            setAlert("Changes sent to graph API successfully", "success");

            // STEP 3: Fetch updated graph data (only after update-graph succeeds)
            try {
              const graphDataResp: any = await fetchPythonGraphData({ repo_id: "prospo-alpha", client_id: clientId });

              if (
                graphDataResp?.success &&
                graphDataResp.data?.graph_data?.clientGroup?.client_group
              ) {
                setAlert(
                  "Updated graph data fetched successfully",
                  "success"
                );

                const graphData = graphDataResp.data.graph_data;
                const categories = schema?.factfindTemplate?.categories || [];

                // Transform updated graph data to payload
                const updatedPayload = transformGraphDataToPayload(
                  categories,
                  graphData.clientGroup.client_group
                );
                const cleanUpdatedPayload =
                  normalizeAndSortData(updatedPayload);

                // STEP 4: Save the graph-updated data as another version
                const graphUpdatedVersion = await saveFactFind(clientId, { factFindData: cleanUpdatedPayload, clientGraphData: clientGraph });

                if (graphUpdatedVersion) {
                  setFactFind(graphUpdatedVersion);
                  setAlert(
                    "Graph-updated data saved successfully! All operations completed.",
                    "success"
                  );
                }
              } else {
                setAlert(
                  "Failed to fetch updated graph data. Graph may not have updated correctly.",
                  "warning"
                );
              }
            } catch (graphFetchError: any) {
              console.error("Error fetching graph data:", graphFetchError);
              setAlert(
                `Failed to fetch updated graph data: ${
                  graphFetchError.message || "Unknown error"
                }`,
                "warning"
              );
            }
          } catch (updateGraphError: any) {
            console.error("Error updating graph:", updateGraphError);
            setAlert(
              `Failed to send changes to graph API: ${
                updateGraphError.message || "Unknown error"
              }`,
              "warning"
            );
          }
        }
      } else {
        setAlert("Failed to save data. Please try again.", "error");
      }
    } catch (error: any) {
      console.error("Error in handleSaveAndUpdate:", error);
      setAlert(
        `Failed to save and update: ${error.message || "Unknown error"}`,
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = (newActiveVersion: any) => {
    setFactFind(newActiveVersion);
    setChangedFieldsList([]); // Clear tracked changes on restore
    setAlert("Version restored successfully.", "success");
  };

  // Helper to get changed fields for Send button
  const getChangedFieldsPayload = () => {
    if (!schema || changedFieldsList.length === 0) return [];

    const categories = schema?.factfindTemplate?.categories || [];
    const changedFields: any[] = [];

    // Build a lookup of all fields in the schema with their metadata
    const fieldMetadataLookup = new Map<string, any>();

    const buildFieldLookup = (
      fields: any[],
      blockKey: string,
      parentPath: string = ""
    ) => {
      fields.forEach((field: any) => {
        if (field.subsection_name && Array.isArray(field.fields)) {
          const subKey = getMongooseKey(field.subsection_name);
          buildFieldLookup(field.fields, blockKey, parentPath + "." + subKey);
        } else if (field.displayLabel) {
          const key = getMongooseKey(field.displayLabel);
          const fullPath = parentPath ? parentPath + "." + key : key;
          fieldMetadataLookup.set(`${blockKey}.${fullPath}`, {
            jsonPath: field.jsonPath || "",
            graphLabel: field.graphLabel || "",
            displayLabel: field.displayLabel,
          });
        }
      });
    };

    categories.forEach((category: any) => {
      if (!category.blocks) return;
      category.blocks.forEach((block: any) => {
        const blockKey = getMongooseKey(block.section);
        buildFieldLookup(block.fields, blockKey);
      });
    });

    // Now process only the changed fields
    changedFieldsList.forEach(({ blockKey, path, value }) => {
      const fieldPathKey = `${blockKey}.${path.join(".")}`;
      const metadata = fieldMetadataLookup.get(fieldPathKey);

      if (metadata && value && value !== "N/A") {
        changedFields.push({
          jsonPath: metadata.jsonPath,
          graphLabel: metadata.graphLabel,
          value: String(value),
        });
      }
    });

    console.log("📤 Changed Fields Payload:", changedFields);
    return changedFields;
  };

  const detailsFields = useMemo(() => {
    const factfindtemplate = schema?.factfindTemplate?.categories || [];
    // Dynamic: return all categories as an array for tab rendering
    return {
      // Keep legacy named fields for backward compat
      personalDetails: factfindtemplate.find((cat: any) => cat.name === "PERSONAL DETAILS")?.blocks,
      riskProfile: factfindtemplate.find((cat: any) => cat.name === "RISK PROFILE")?.blocks,
      balanceSheet: factfindtemplate.find((cat: any) => cat.name === "BALANCE SHEET")?.blocks,
      goals: factfindtemplate.find((cat: any) => cat.name === "GOALS")?.blocks,
      cashflow: factfindtemplate.find((cat: any) => cat.name === "CASHFLOW")?.blocks,
      insurance: factfindtemplate.find((cat: any) => cat.name === "INSURANCE")?.blocks,
      // All categories for dynamic tab rendering
      allCategories: factfindtemplate,
    };
  }, [schema]);

  const isFieldsLoaded = !loading && factFind && (detailsFields.allCategories?.length > 0 || detailsFields.personalDetails);

  return (
    <>
      <Box sx={{ width: "100%", p: 2 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <BackButton />
          <Typography variant="h5" fontWeight="bold">
            Fact Find Form
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <ImportFactFindModal
              clientId={clientId ?? ""}
              onImported={(changed) => {
                // Nothing new was written (already-imported / no-op) → don't reload.
                if (!changed) return;
                // Clear current data so the skeleton shows while the freshly-imported
                // values load (the graph re-fetch takes a few seconds).
                setClientGraph(null);
                fetchClientGraph();
              }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={isDownloading || !clientGraph?.data?.length}
              endIcon={isDownloading ? <CircularProgress size={13} color="inherit" /> : <ExpandMoreIcon fontSize="small" />}
              onClick={(e) => setDownloadAnchor(e.currentTarget)}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Download
            </Button>
            <Menu
              anchorEl={downloadAnchor}
              open={Boolean(downloadAnchor)}
              onClose={() => setDownloadAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => handleDownload("docx")} sx={{ fontSize: "0.875rem" }}>
                Download as DOCX
              </MenuItem>
              <MenuItem onClick={() => handleDownload("pdf")} sx={{ fontSize: "0.875rem" }}>
                Download as PDF
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Client Graph — Classes & Subclasses */}
        {!clientGraph && (loading || clientGraphLoading) ? (
          <FactFindFormSkeleton />
        ) : clientGraph ? (
          <ClientGraphTree
            data={clientGraph}
            orgId={login?.data?.orgId ?? ""}
            clientId={clientId ?? ""}
            onRefresh={fetchClientGraph}
            onPersist={async (updatedGraph: any) => {
              await saveFactFind(clientId, { factFindData: factFind?.factFindData ?? null, clientGraphData: updatedGraph });
            }}
          />
        ) : null}
      </Box>

      {/* Render the Version History Drawer */}
      <FactFindVersionHistoryDrawer
        open={isVersionDrawerOpen}
        onClose={() => setIsVersionDrawerOpen(false)}
        clientId={clientId}
        schema={schema}
        onRestoreVersion={handleRestore}
      />
    </>
  );
};

export default ClientDetails;
